// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { getSharedBffWebSocket } from '@/services/bffWebSocket';

const DEFAULT_TIMEOUT_MS = 10000;

const createRequestId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `invite-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const buildTourInviteRouteUrl = (inviteToken: string) => {
  const configuredFrontendBaseUrl = process.env.EXPO_PUBLIC_FRONTEND_BASE_URL;
  const webOrigin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : null;
  const frontendBaseUrl = (configuredFrontendBaseUrl ?? webOrigin ?? 'https://goffy.app').replace(/\/+$/, '');
  return `${frontendBaseUrl}/tour/invite/${encodeURIComponent(inviteToken)}`;
};

export const resolveTourInviteLink = async ({
  apiBaseUrl,
  tourId,
  authToken,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}: {
  apiBaseUrl: string;
  tourId: string;
  authToken?: string | null;
  timeoutMs?: number;
}) => {
  const socket = getSharedBffWebSocket(apiBaseUrl);
  socket.setAuthToken(authToken ?? null);

  const requestId = createRequestId();

  return new Promise<string>((resolve, reject) => {
    let settled = false;
    const timeout = setTimeout(() => {
      if (settled) {
        return;
      }
      settled = true;
      unsubscribe();
      reject(new Error('invite lookup timeout'));
    }, timeoutMs);

    const finish = (result: { value?: string; error?: string }) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timeout);
      unsubscribe();
      if (result.error) {
        reject(new Error(result.error));
        return;
      }
      if (!result.value) {
        reject(new Error('missing invite token'));
        return;
      }
      resolve(result.value);
    };

    const unsubscribe = socket.onMessage((message) => {
      const payload = message as {
        type?: string;
        requestId?: string;
        inviteToken?: string;
        message?: string;
        code?: string;
      };
      if (!payload.type || payload.requestId !== requestId) {
        return;
      }

      if (payload.type === 'tour.invite') {
        const inviteToken = payload.inviteToken?.trim();
        finish({
          value: inviteToken ? buildTourInviteRouteUrl(inviteToken) : undefined,
        });
        return;
      }

      if (payload.type === 'tour.invite.error') {
        finish({
          error: payload.message ?? payload.code ?? 'invite lookup failed',
        });
      }
    });

    socket.sendTourInviteRequest(tourId, requestId);
  });
};
